import { Link } from "@tanstack/react-router";
import { useI18n } from "@/lib/i18n";
import { translatePortuguese } from "@/data/portuguese-core";

const LBL: Record<string, { en: string; pt: string; es: string }> = {
  "Works": {en:"Works",pt:"Trabalhos",es:"Trabajos"},
  "Bio": {en:"Bio",pt:"Bio",es:"Bio"},
  "Contact": {en:"Contact",pt:"Contato",es:"Contacto"},
  "Sound": {en:"Sound",pt:"Som",es:"Sonido"},
  "Photo": {en:"Photo",pt:"Foto",es:"Foto"},
  "Performance": {en:"Performance",pt:"Performance",es:"Performance"},
  "Science": {en:"Science",pt:"Ciência",es:"Ciencia"},
  "Write": {en:"Write",pt:"Escrita",es:"Escritura"},
  "Clipping": {en:"Clipping",pt:"Clipping",es:"Prensa"},
  "CV": {en:"CV",pt:"CV",es:"CV"},
  "Menu": {en:"Menu",pt:"Menu",es:"Menú"},
  "Elsewhere": {en:"Elsewhere",pt:"Em outros lugares",es:"En otros lugares"},
  "Themes": {en:"Themes",pt:"Temas",es:"Temas"},
  "Decolonial Art": {en:"Decolonial Art",pt:"Arte decolonial",es:"Arte decolonial"},
};
import { useEffect, useState } from "react";

import { LanguageMenu } from "@/components/language-menu";

const externalLinks = [
  { label: "Performance", href: "/performance" },
  { label: "Science", href: "/science" },

  { label: "Write", href: "/write" },
];

const socialLinks = [
  { label: "Instagram", href: "https://www.instagram.com/luizajesusdoprado" },
  { label: "Twitter", href: "https://twitter.com/luizajdoprado" },
  { label: "LinkedIn", href: "https://www.linkedin.com/in/luizajesusdoprado" },
  { label: "Facebook", href: "https://www.facebook.com/luizajesusdoprado" },
];

const internalLinks = [
  { label: "Bio", to: "/about" },
  { label: "Clipping", to: "/clipping" },
  { label: "CV", to: "/cv" },
  { label: "Contact", to: "/contact" },
] as const;

export function HomeNav({ tone = "dark" }: { tone?: "dark" | "light" } = {}) {
  const { tx, lang } = useI18n();
  const c = tone === "light" ? "text-ink" : "text-foreground";
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  return (
    <>
      <header
        className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
          scrolled && !open ? "bg-background/15 backdrop-blur-md" : "bg-transparent"
        }`}
      >
        <div className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-4 px-4 py-5 md:px-[6vw] md:py-6">
          <Link
            to="/"
            className={`inline-flex min-h-11 min-w-0 items-center truncate font-sans text-[8px] uppercase leading-none tracking-[0.18em] md:text-[10px] md:tracking-[0.22em] ${
              open ? "text-bone" : c
            }`}
          >
            Luiza&nbsp;Jesus&nbsp;do&nbsp;Prado
          </Link>

          <div className="flex shrink-0 items-center gap-4 md:gap-8">
            <Link
              to="/guest-session/login"
              onClick={() => setOpen(false)}
              className={`inline-flex min-h-11 items-center border border-current/40 px-3 py-2 font-mono text-[8px] uppercase leading-none tracking-[0.22em] opacity-70 transition-opacity hover:opacity-100 md:text-[9px] ${
                open ? "text-bone" : c
              }`}
            >
              {translatePortuguese("Guest", lang)}
            </Link>
            <LanguageMenu tone={open ? "bone" : tone === "light" ? "ink" : "bone"} />
            <button
              type="button"
              onClick={() => setOpen((v) => !v)}
              aria-label={translatePortuguese(open ? "Close menu" : "Open menu", lang)}
              aria-expanded={open}
              className={`flex h-11 w-11 flex-col items-center justify-center gap-[6px] ${open ? "text-bone" : c}`}
            >
              <span
                className={`block h-px w-6 bg-current transition-transform duration-300 ${
                  open ? "translate-y-[3.5px] rotate-45" : ""
                }`}
              />
              <span
                className={`block h-px w-6 bg-current transition-transform duration-300 ${
                  open ? "-translate-y-[3.5px] -rotate-45" : ""
                }`}
              />
            </button>
          </div>
        </div>
      </header>

      <div
        className={`fixed inset-0 z-40 bg-ink transition-opacity duration-500 ${
          open ? "opacity-100" : "pointer-events-none opacity-0"
        }`}
      >
        <nav className="flex h-full flex-col items-center justify-center gap-3 overflow-y-auto px-6 py-24 text-center md:gap-5">
          <Link
            to="/works"
            onClick={() => setOpen(false)}
            className="font-didone text-[clamp(1.35rem,5vw,2.8rem)] font-light leading-[1.15] text-bone/70 transition-colors hover:text-bone"
          >
            {tx({en:"Works",pt:"Trabalhos",es:"Trabajos"})}
          </Link>
          {internalLinks.map((l) => (
            <Link
              key={tx(LBL[l.label] ?? {en:l.label,pt:l.label,es:l.label})}
              to={l.to}
              onClick={() => setOpen(false)}
              className="font-didone text-[clamp(1.35rem,5vw,2.8rem)] font-light leading-[1.15] text-bone/70 transition-colors hover:text-bone"
            >
              {tx(LBL[l.label] ?? {en:l.label,pt:l.label,es:l.label})}
            </Link>
          ))}

          <span className="mt-6 h-px w-16 bg-bone/20" />

          <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2">
            <Link
              to="/sound"
              onClick={() => setOpen(false)}
              className="font-sans text-[13px] font-light uppercase tracking-[0.2em] text-bone/60 transition-colors hover:text-bone md:text-[15px]"
            >
              {tx({en:"Sound",pt:"Som",es:"Sonido"})}
            </Link>
            <Link
              to="/photo"
              onClick={() => setOpen(false)}
              className="font-sans text-[13px] font-light uppercase tracking-[0.2em] text-bone/60 transition-colors hover:text-bone md:text-[15px]"
            >
              {tx({en:"Photo",pt:"Foto",es:"Foto"})}
            </Link>
            {externalLinks.map((l) => (
              <a
                key={tx(LBL[l.label] ?? {en:l.label,pt:l.label,es:l.label})}
                href={l.href}
                className="font-sans text-[13px] font-light uppercase tracking-[0.2em] text-bone/60 transition-colors hover:text-bone md:text-[15px]"
              >
                {tx(LBL[l.label] ?? {en:l.label,pt:l.label,es:l.label})}
              </a>
            ))}
          </div>

          <div className="mt-4 flex flex-wrap items-center justify-center gap-x-5 gap-y-2">
            {socialLinks.map((l) => (
              <a
                key={tx(LBL[l.label] ?? {en:l.label,pt:l.label,es:l.label})}
                href={l.href}
                target="_blank"
                rel="noreferrer"
                className="font-sans text-[10px] font-light uppercase tracking-[0.18em] text-bone/65 transition-colors hover:text-bone"
              >
                {tx(LBL[l.label] ?? {en:l.label,pt:l.label,es:l.label})}
              </a>
            ))}
          </div>
        </nav>
      </div>
    </>
  );
}
